import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';

import type { Tables } from '@archetelos/db';

import { AdminHeader, FilterTabs, StatusBadge } from '@/features/admin/ui';
import { useRequireRole } from '@/lib/auth/useRequireRole';
import { auditLog } from '@/lib/queries/admin';
import { supabase } from '@/lib/supabase';

type SiteCopy = Tables<'site_copy'>;
type FaqItem = Tables<'faq_items'>;

// Institutional copy editor (Spec 25). Admin-only (System group); moderators
// 403 on direct nav. Editing writes the draft; Publish copies draft → published
// and — via the site_copy/faq_items DB webhook — fires the marketing rebuild.
// v1 edits the text of the seeded slots + FAQ items; adding / reordering /
// removing FAQ items is a tracked follow-up (the marketing template owns order
// so the seeded build stays byte-identical to today).

const copyQueries = {
  siteCopy: () =>
    queryOptions({
      queryKey: ['admin', 'site_copy'] as const,
      queryFn: async (): Promise<SiteCopy[]> => {
        const { data, error } = await supabase
          .from('site_copy')
          .select('*')
          .order('page', { ascending: true })
          .order('display_order', { ascending: true });
        if (error) throw error;
        return (data ?? []) as SiteCopy[];
      },
    }),
  faqItems: () =>
    queryOptions({
      queryKey: ['admin', 'faq_items'] as const,
      queryFn: async (): Promise<FaqItem[]> => {
        const { data, error } = await supabase
          .from('faq_items')
          .select('*')
          .order('section', { ascending: true })
          .order('display_order', { ascending: true });
        if (error) throw error;
        return (data ?? []) as FaqItem[];
      },
    }),
};

const TABS = [
  { value: 'about', label: 'About' },
  { value: 'membership', label: 'Membership' },
  { value: 'home', label: 'Home' },
  { value: 'faq', label: 'FAQ' },
] as const;
type Tab = (typeof TABS)[number]['value'];

export const Route = createFileRoute('/admin/copy')({
  component: CopyPage,
});

function CopyPage() {
  useRequireRole(['admin']); // admin-only; moderators 403 (Spec 21)
  const [tab, setTab] = useState<Tab>('about');

  return (
    <div className="mx-auto max-w-4xl">
      <AdminHeader kicker="Admin · System" title="Copy" />
      <p className="mt-3 max-w-2xl font-sans text-sm text-ink/60">
        Institutional prose and the FAQ. Save a draft to work in private; Publish to bake it to the
        public site on the next rebuild. Structural blocks (bulleted lists, links, contact details,
        and the live-priced answer) stay in the templates and are not edited here.
      </p>

      <div className="mt-8">
        <FilterTabs options={TABS} value={tab} onChange={setTab} />
      </div>

      <div className="mt-8">{tab === 'faq' ? <FaqTab /> : <ProseTab page={tab} />}</div>
    </div>
  );
}

// ── Prose tab ──────────────────────────────────────────────────────────────
function ProseTab({ page }: { page: Exclude<Tab, 'faq'> }) {
  const list = useQuery(copyQueries.siteCopy());
  if (list.isLoading) return <p className="font-sans text-sm text-ink/50">Loading…</p>;
  if (list.error)
    return <p className="font-sans text-sm text-oxblood">Failed to load copy slots.</p>;
  const rows = (list.data ?? []).filter((r) => r.page === page);
  if (rows.length === 0)
    return <p className="font-sans text-sm text-ink/50">No slots for this page.</p>;
  return (
    <div className="space-y-8">
      {rows.map((row) => (
        <ProseRow key={row.key} row={row} />
      ))}
    </div>
  );
}

function ProseRow({ row }: { row: SiteCopy }) {
  const qc = useQueryClient();
  const [value, setValue] = useState(row.draft_value ?? row.published_value ?? '');
  const pendingSeed = (row.draft_value ?? '') !== (row.published_value ?? '');
  const dirty = value !== (row.draft_value ?? row.published_value ?? '');
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'site_copy'] });

  const saveDraft = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('site_copy')
        .update({ draft_value: value })
        .eq('key', row.key);
      if (error) throw error;
      await auditLog('copy.draft_saved', 'site_copy', null, { key: row.key });
    },
    onSuccess: () => {
      toast.success('Draft saved.');
      invalidate();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Save failed.'),
  });

  const publish = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('site_copy')
        .update({ published_value: value, draft_value: value })
        .eq('key', row.key);
      if (error) throw error;
      await auditLog('copy.published', 'site_copy', null, { key: row.key });
    },
    onSuccess: () => {
      toast.success('Published — the site rebuilds shortly.');
      invalidate();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Publish failed.'),
  });

  const busy = saveDraft.isPending || publish.isPending;

  return (
    <div className="rounded-md border border-ink/10 bg-parchment p-5">
      <div className="flex items-center justify-between gap-3">
        <label className="font-sans text-xs uppercase tracking-[0.12em] text-ink/60">
          {row.label}
        </label>
        <StatusBadge status={pendingSeed || dirty ? 'draft' : 'published'} />
      </div>
      <textarea
        rows={Math.min(8, Math.max(2, Math.ceil(value.length / 90)))}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="mt-3 w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 font-sans text-sm text-ink focus:border-oxblood focus:outline-none focus:ring-2 focus:ring-oxblood/30"
      />
      {value.trim() && (
        <div className="mt-3 max-h-40 overflow-y-auto whitespace-pre-line rounded-md border border-ink/10 bg-ink/[0.02] p-4 font-sans text-sm text-ink">
          {value}
        </div>
      )}
      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          disabled={busy || !dirty}
          onClick={() => saveDraft.mutate()}
          className="rounded-md border border-ink/30 px-4 py-2 font-sans text-sm text-ink hover:border-oxblood hover:text-oxblood disabled:opacity-40"
        >
          Save draft
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => publish.mutate()}
          className="rounded-md bg-ink px-4 py-2 font-sans text-sm font-medium text-parchment hover:bg-oxblood disabled:opacity-40"
        >
          Publish
        </button>
      </div>
    </div>
  );
}

// ── FAQ tab ──────────────────────────────────────────────────────────────
const SECTION_LABELS: Record<string, string> = {
  about: 'About the Platform',
  editorial: 'Editorial Position',
  contributor: 'For Contributors',
  reader: 'For Readers and Subscribers',
  practical: 'Practical',
};

function FaqTab() {
  const list = useQuery(copyQueries.faqItems());
  if (list.isLoading) return <p className="font-sans text-sm text-ink/50">Loading…</p>;
  if (list.error) return <p className="font-sans text-sm text-oxblood">Failed to load FAQ.</p>;
  const rows = list.data ?? [];
  const sections = [...new Set(rows.map((r) => r.section))];
  return (
    <div className="space-y-10">
      {sections.map((section) => (
        <div key={section}>
          <h2 className="font-sans text-xs font-semibold uppercase tracking-[0.18em] text-ink/50">
            {SECTION_LABELS[section] ?? section}
          </h2>
          <div className="mt-4 space-y-8">
            {rows
              .filter((r) => r.section === section)
              .map((row) => (
                <FaqRow key={row.id} row={row} />
              ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function FaqRow({ row }: { row: FaqItem }) {
  const qc = useQueryClient();
  const [question, setQuestion] = useState(row.draft_question ?? row.question);
  const [answer, setAnswer] = useState(row.draft_answer_md ?? row.answer_md);
  const dirty =
    question !== (row.draft_question ?? row.question) ||
    answer !== (row.draft_answer_md ?? row.answer_md);
  const pendingSeed =
    (row.draft_question ?? row.question) !== row.question ||
    (row.draft_answer_md ?? row.answer_md) !== row.answer_md;
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'faq_items'] });

  const saveDraft = useMutation({
    mutationFn: async () => {
      if (!question.trim() || !answer.trim()) throw new Error('Question and answer are required.');
      const { error } = await supabase
        .from('faq_items')
        .update({ draft_question: question, draft_answer_md: answer })
        .eq('id', row.id);
      if (error) throw error;
      await auditLog('copy.faq_draft_saved', 'faq_items', row.id, { key: row.key });
    },
    onSuccess: () => {
      toast.success('Draft saved.');
      invalidate();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Save failed.'),
  });

  const publish = useMutation({
    mutationFn: async () => {
      if (!question.trim() || !answer.trim()) throw new Error('Question and answer are required.');
      const { error } = await supabase
        .from('faq_items')
        .update({
          question,
          answer_md: answer,
          draft_question: question,
          draft_answer_md: answer,
          is_published: true,
        })
        .eq('id', row.id);
      if (error) throw error;
      await auditLog('copy.faq_published', 'faq_items', row.id, { key: row.key });
    },
    onSuccess: () => {
      toast.success('Published — the site rebuilds shortly.');
      invalidate();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Publish failed.'),
  });

  const busy = saveDraft.isPending || publish.isPending;

  return (
    <div className="rounded-md border border-ink/10 bg-parchment p-5">
      <div className="flex items-center justify-between gap-3">
        <StatusBadge status={pendingSeed || dirty ? 'draft' : 'published'} />
      </div>
      <label className="mt-2 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60">
        Question
      </label>
      <input
        type="text"
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        className="mt-1 w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 font-serif text-base text-ink focus:border-oxblood focus:outline-none focus:ring-2 focus:ring-oxblood/30"
      />
      <label className="mt-3 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60">
        Answer
      </label>
      <textarea
        rows={Math.min(8, Math.max(3, Math.ceil(answer.length / 90)))}
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        className="mt-1 w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 font-sans text-sm text-ink focus:border-oxblood focus:outline-none focus:ring-2 focus:ring-oxblood/30"
      />
      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          disabled={busy || !dirty}
          onClick={() => saveDraft.mutate()}
          className="rounded-md border border-ink/30 px-4 py-2 font-sans text-sm text-ink hover:border-oxblood hover:text-oxblood disabled:opacity-40"
        >
          Save draft
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => publish.mutate()}
          className="rounded-md bg-ink px-4 py-2 font-sans text-sm font-medium text-parchment hover:bg-oxblood disabled:opacity-40"
        >
          Publish
        </button>
      </div>
    </div>
  );
}
